import { lazy, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTitle } from 'react-use';
import { useFlag } from '@unleash/proxy-client-react';
import classNames from 'classnames';

import {
  selectLatestHealthMetrics,
  selectOrchestrate,
  selectSelectedPricePoint,
  selectSignUp
} from 'store';
import { AppointmentItem } from 'store/appointments/appointments.types';

import AppointmentTimerBanner from 'features/AppointmentTimerBanner';
import Loader from 'shared/Loader';
import { notifyToast } from 'shared/Toast/Toast';
import Header from 'widgets/Header';
import DiscountBanner from 'widgets/signUp/DiscountBanner';

import {
  BENEFITS_CHECK_TT_FLOW_PART,
  BENEFITS_CHECK_WM_FLOW_PART,
  WEIGHT_MANAGEMENT_INSURANCE_BENEFITS_CHECK_NEW_FLOW_STEPS
} from 'constants/onboarding';
import { useAppSelector, useQuery } from 'hooks';
import useContinueInWeb from 'hooks/useContinueInWeb';
import { useOnboarding } from 'hooks/useOnboarding';
import { socketClose, socketInit } from 'socket';
import socketMessages from 'socket/socketMessages';
import { FeatureFlag, FlowTypes, PathName, SignUpSteps } from 'utils/enums';
import {
  calculateBMI,
  checkIfPromoCodeIsValid,
  getDiscountToShow,
  getPaymentAmount,
  isPrimaryCareFlow,
  lazyRetry
} from 'utils/helpers';
import { handlePostHogTracking } from 'utils/posthog';

import {
  getContainerWidth,
  getInitialBMIData,
  getProgressInfo,
  isShowBackButtonFunc,
  isShowHeaderLogos
} from './signup.settings';
import { AdditionalConditions } from './signUp.types';

const Content = lazy(() => lazyRetry(() => import('containers/SignUp/Content')));

const SignUp = () => {
  useTitle('LifeMD - Sign Up');
  const { stepName, steps, moveToStep, flow, isBackAnimation, currentStep, isLoading, setSteps } =
    useOnboarding();

  const navigate = useNavigate();
  const query = useQuery();

  const isProgressHidden = query.get('hideProgress') === 'true';
  const isHeaderHidden = query.get('hideHeader') === 'true';

  const {
    user,
    promoVisited,
    membershipPlans,
    firstAppointment: { _id: firstAppointmentId, categoryDisplayName },
    signupOptions
  } = useAppSelector(selectSignUp);
  const {
    appointment: { appointmentTypeId },
    mif: { bmi: storedBMI },
    mif_qa: mifQA,
    user: orchUser,
    payment: { product_price_point_handle: productPricePointHandle, product_handle }
  } = useAppSelector(selectOrchestrate);
  const { height, weight } = useAppSelector(selectLatestHealthMetrics);
  const selectedPP = useAppSelector(selectSelectedPricePoint);
  const isSteadymdProvidersEnable = useFlag(FeatureFlag.SteadymdProviders);

  const [waitTime, setWaitTime] = useState('10-15 min');

  const showBanner = [
    SignUpSteps.Intro,
    SignUpSteps.InitialInfo,
    SignUpSteps.AppointmentCategory,
    SignUpSteps.AppointmentDetails,
    SignUpSteps.ChooseProvider,
    SignUpSteps.CreateNewUser,
    SignUpSteps.PlanSelect,
    SignUpSteps.Summary
  ].includes(stepName);

  const continueInWeb = useContinueInWeb(user);

  const isShowBackButton = isShowBackButtonFunc(stepName, flow, currentStep);
  const showMiddleLogos = isShowHeaderLogos({ flow, step: stepName });

  const currentSelectedPlan = membershipPlans?.find((p) => p._id === user.planId);
  const isValidPromoCode =
    !currentSelectedPlan || !selectedPP
      ? false
      : checkIfPromoCodeIsValid(
          promoVisited,
          flow,
          currentSelectedPlan.planCode,
          selectedPP.paymentInterval.qty,
          signupOptions?.payWithInsurance
        );

  const { discountPercentage, discountValue } = getPaymentAmount(
    isValidPromoCode ? promoVisited : '',
    selectedPP,
    currentSelectedPlan?.planCode,
    true
  );

  const handleAdditionalConditions = (type: AdditionalConditions, value: boolean) => {
    let newStep: SignUpSteps | '' = '';
    switch (type) {
      // feel free to rename this condition
      case 'injectable-vs-oral':
        let newSteps = [];
        // lets keep value true if user stay on glp-1 flow and false if he switch to tt
        if (value) {
          // Build GLP-1 FLOW
          newSteps = [
            ...WEIGHT_MANAGEMENT_INSURANCE_BENEFITS_CHECK_NEW_FLOW_STEPS,
            ...BENEFITS_CHECK_WM_FLOW_PART
          ];
        } else {
          // Build Triple Therapy FLOW
          newSteps = [
            ...WEIGHT_MANAGEMENT_INSURANCE_BENEFITS_CHECK_NEW_FLOW_STEPS,
            ...BENEFITS_CHECK_TT_FLOW_PART
          ];
        }
        setSteps(newSteps);
        return moveToStep('next');
      case 'bmi-labs':
        if (flow === FlowTypes.TripleTherapy) {
          break;
        }
        if (!value) {
          setSteps(
            steps.filter((step) => ![SignUpSteps.WMLabWork, SignUpSteps.SelectLabs].includes(step))
          );
        } else if (
          !steps.includes(SignUpSteps.WMLabWork) ||
          !steps.includes(SignUpSteps.SelectLabs)
        ) {
          const newSteps = [...steps];
          newSteps.splice(currentStep + 1, 0, SignUpSteps.WMLabWork, SignUpSteps.SelectLabs);
          setSteps(newSteps);
        }
        break;
      case 'ash-selected':
        // IF user selected Ash kit
        if (value) {
          setSteps(steps.filter((step) => ![SignUpSteps.SelectLabs].includes(step)));
        } else if (!steps.includes(SignUpSteps.SelectLabs)) {
          const newSteps = [...steps];
          newSteps.splice(currentStep + 1, 0, SignUpSteps.SelectLabs);
          setSteps(newSteps);
        }
        break;
      case 'appt-type':
        // if value is true - we can skip book appointment and go to the checkout
        if (value) {
          newStep = SignUpSteps.WMCheckout;
        } else {
          return moveToStep('next');
        }
        break;
      case 'insurance':
        if (value) {
          return moveToStep('next');
        } else {
          if (
            [
              FlowTypes.WeightManagementFlowInsuranceAlt,
              FlowTypes.WeightManagementFlowInsuranceDiscovery
            ].includes(flow)
          ) {
            newStep =
              orchUser.state === 'California'
                ? SignUpSteps.WMCongrats
                : SignUpSteps.InsuranceForMedications;
          } else {
            newStep = [FlowTypes.WeightManagementFlowInsurance].includes(flow)
              ? SignUpSteps.CheckPrescriptionCoverage
              : SignUpSteps.VerifyIdentity;
          }
        }
        break;
      case 'insurance-for-medications':
        if (!value) {
          return moveToStep('next');
        } else {
          newStep = SignUpSteps.WMCongrats;
        }
        break;
      case 'medications-tbd':
        newStep = SignUpSteps.WMCongrats;
        break;
      case 'will-use-insurance':
        if (value) {
          return moveToStep('next');
        } else {
          newStep = SignUpSteps.InsuranceForMedications;
        }
        break;
      case 'wm-you-are-in-good-hands':
        if (value && storedBMI.value !== 0) {
          const data = getInitialBMIData(mifQA, height, weight);
          const BMI = Number(calculateBMI(data));

          if (signupOptions.flowVersion === 'TT' && BMI >= 27) {
            setSteps(steps.filter((step) => ![SignUpSteps.WMHeightAndWeight].includes(step)));

            moveToStep('next');
            break;
          }
          if (BMI < 25) {
            moveToStep('next', { searchParams: '?hideProgress=true&subStep=low-bmi&a=none' });
          }
          if (BMI >= 25 && BMI < 27) {
            moveToStep('next', {
              searchParams: '?subStep=tt-therapy&a=none&shouldEmulateBack=true'
            });
          }
          if (BMI >= 27 && BMI < 30) {
            moveToStep('next', { searchParams: '?subStep=extra-question&shouldEmulateBack=true' });
          }
          if (BMI >= 30) {
            moveToStep('next', { searchParams: '?subStep=extra-question&shouldEmulateBack=true' });
          }
        } else {
          moveToStep('next');
        }
        break;
      case 'benefits-tt':
        if (value) {
          setSteps([
            ...WEIGHT_MANAGEMENT_INSURANCE_BENEFITS_CHECK_NEW_FLOW_STEPS,
            ...BENEFITS_CHECK_TT_FLOW_PART
          ]);
          return moveToStep('next');
        }
        break;
      default:
        return moveToStep('next');
    }
    if ((type === 'bmi-labs' && !value) || type === 'ash-selected' || !newStep) {
      return;
    }
    const index = steps.indexOf(newStep);
    moveToStep(index, { searchParams: '?shouldEmulateBack=true' });
  };

  const handleBackNav = () => {
    const isProgramNavigate = query.get('shouldEmulateBack') === 'true';

    if (isProgressHidden || isProgramNavigate) {
      moveToStep(currentStep, { programNavigate: true });
    } else {
      moveToStep('prev');
    }
  };

  const handleUpdatedAccessToken = () => {
    if (user.accessToken) {
      socketInit(socketMessages, user.accessToken);
      if (firstAppointmentId) {
        socketMessages.on('appointmentUpdate', (data: AppointmentItem) => {
          setWaitTime(data.displayWaitTime);
        });
        socketMessages.on('queueOnboardedEarly', () => {
          notifyToast('You’ve been moved to the waiting room by a medical assistant.');
          continueInWeb(firstAppointmentId);
        });
      }
    } else {
      socketClose(socketMessages);
    }
    return () => {
      socketMessages.off('appointmentUpdate');
      socketMessages.off('onboardedEarly');
      socketClose(socketMessages);
    };
  };

  const showTimer =
    [SignUpSteps.SCResults, SignUpSteps.SwitchToApp].includes(stepName) &&
    flow === FlowTypes.QueueFlow &&
    user.isInstantAppointment;
  const headerWrapperClassName = classNames(
    'flex flex-col-reverse md:flex-col md:mb-8',
    { 'pointer-events-none': isLoading }
    //   , {
    //   'max-md:fixed max-md:mb-20 max-md:w-full max-md:bg-white max:md:z-20':
    //     stepName === SignUpSteps.Agreement
    // }
  );
  const wrapperClassName = classNames(
    'w-full mx-auto flex flex-col max-md:grow max-md:p-4 max-md:pb-6',
    { 'pointer-events-none': isLoading },
    // { 'max-md:mt-[112px]': stepName === SignUpSteps.Agreement },
    { 'md:pb-8': stepName !== SignUpSteps.WMCompleteOnboarding },
    getContainerWidth(stepName, flow)
  );

  const { currStep, stepsAmount } = getProgressInfo(currentStep + 1, steps);

  const isProgressbarHidden = isProgressHidden || stepName === SignUpSteps.MarketingVideo;

  useEffect(() => {
    handlePostHogTracking(stepName, {
      appointmentType: categoryDisplayName || appointmentTypeId,
      membershipPlan: user.planCode || product_handle,
      planPricePointId: productPricePointHandle || selectedPP?.planPricePointId || '',
      state: orchUser.state || user.doctorLicenseState,
      userID: user._id || orchUser?._id || ''
    });
  }, [stepName]);

  useEffect(() => {
    if (isPrimaryCareFlow(flow) && isSteadymdProvidersEnable) {
      navigate(PathName.CreateAppointment);
    }
  }, [flow, isSteadymdProvidersEnable]);

  useEffect(handleUpdatedAccessToken, [user.accessToken, firstAppointmentId]);

  return (
    <div className="flex min-h-screen flex-col bg-gray-100">
      {showTimer && <AppointmentTimerBanner displayWaitTime={waitTime} />}
      <Loader isVisible={isLoading} />
      {isValidPromoCode && showBanner && (
        <DiscountBanner
          discount={getDiscountToShow(discountPercentage, discountValue)}
          flow={flow}
          promoVisited={promoVisited}
        />
      )}
      <div className={headerWrapperClassName}>
        {!isHeaderHidden && (
          <Header
            flow={flow}
            flowLength={stepsAmount}
            hideStepper={isProgressbarHidden}
            isShowBackButton={isShowBackButton}
            isShowCenterLogos={showMiddleLogos}
            step={currStep}
            onClickPrev={handleBackNav}
          />
        )}
      </div>

      <div className={wrapperClassName}>
        <Content
          isBackAnimation={isBackAnimation}
          moveToStep={moveToStep}
          selectedFlow={flow}
          steps={steps}
          onHandleAdditionalConditions={handleAdditionalConditions}
        />
      </div>
    </div>
  );
};

export default SignUp;
